import { faEyeLowVision } from "@elynx/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InfoOutlined, RadioButtonChecked, RadioButtonUnchecked } from "@mui/icons-material";
import { MenuItem, Stack, Typography } from "@mui/material";
import { JSX, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CheckList, CheckListItemType } from "../../../../../components/check-list/CheckList";
import { ContentContainer } from "../../../../../components/content-container/ContentContainer";
import { ContentSection } from "../../../../../components/content-section/ContentSection";
import NoData from "../../../../../components/no-data/NoData";
import SideSheetFilterInput from "../../../../../components/side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import SideSheetSelect from "../../../../../components/side-sheet/side-sheet-select/SideSheetSelect";
import SideSheet from "../../../../../components/side-sheet/side-sheet/SideSheet";
import { useRouteContext } from "../../../../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../../../../queries/UseQueryNotifications";
import { UICustomizationPresetSortEnum, useUICustomizationPresets } from "../../../../../queries/ui-customization/UseUICustomizationPresets";
import i18n from "../../../../../services/localization-service/i18n";
import { UserFieldType } from "../UserFieldTypes";

const presetSortByOptions: Array<{ value: UICustomizationPresetSortEnum, label: string; }> = [
    { value: UICustomizationPresetSortEnum.NameAsc, label: i18n.t("userRoleAdministration.sortOptionsText.nameAsc") },
    { value: UICustomizationPresetSortEnum.NameDesc, label: i18n.t("userRoleAdministration.sortOptionsText.nameDesc") },
    { value: UICustomizationPresetSortEnum.DescriptionAsc, label: i18n.t("userRoleAdministration.sortOptionsText.descriptionAsc") },
    { value: UICustomizationPresetSortEnum.DescriptionDesc, label: i18n.t("userRoleAdministration.sortOptionsText.descriptionDesc") }
];

export type PresetViewProps = {
    organizationID: number | undefined;
};

export default function PresetView({
    organizationID
}: PresetViewProps): JSX.Element {
    const routeContext = useRouteContext();
    const { t } = useTranslation();
    const [filterText, setFilterText] = useState<string>("");
    const [sortBy, setSortBy] = useState<UICustomizationPresetSortEnum>(UICustomizationPresetSortEnum.NameAsc);

    const {
        getValues,
        setValue
    } = useFormContext<UserFieldType>();

    const {
        data: presetData,
        status: presetStatus,
        refetch: refreshPresets
    } = useQueryNotifications(useUICustomizationPresets)({
        groupID: routeContext.groupID as number,
        organizationID: organizationID as number,
        options: {
            filterText: filterText,
            sortBy: sortBy
        },
        enabled: organizationID !== undefined
    });

    const selectedPresetID = getValues("uiCustomizationPresetID");

    function onPresetClick(item: CheckListItemType) {
        if(item.id === selectedPresetID) {
            setValue("uiCustomizationPresetID", undefined, { shouldDirty: true });
        } else {
            setValue("uiCustomizationPresetID", item.id, { shouldDirty: true });
        }
    }

    const checkListItems: Array<CheckListItemType> = useMemo(() => (presetData?.data ?? []).map(p => ({
        id: p.id,
        key: p.id.toString(),
        displayText: p.name,
        secondaryText: p.description,
        checked: p.id === selectedPresetID,
        active: true,
        avatar: <FontAwesomeIcon icon={faEyeLowVision} />
    })), [
        presetData,
        selectedPresetID
    ]);

    return (
        <ContentContainer>
            <ContentSection>
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                        alignItems: "center",
                        padding: 2,
                        paddingTop: 1,
                        paddingBottom: 1
                    }}>
                    <InfoOutlined />
                    <Typography variant="body1">
                        {t("userEditor.steps.pageVisibility.customizationMode.preset.description")}
                    </Typography>
                </Stack>

                {presetStatus === "success" &&
                    presetData?.totalCount === 0
                    ? <NoData
                        title=""
                        text={t("uiCustomizationPresetAdministration.tabs.presets.noData.title")}
                    />
                    : presetData?.filteredCount === 0
                        ? <NoData
                            title=""
                            text={t("uiCustomizationPresetAdministration.tabs.presets.noFilteredData.title")}
                        />
                        : undefined
                }

                <CheckList
                    items={checkListItems}
                    icon={<RadioButtonUnchecked />}
                    checkedIcon={<RadioButtonChecked />}
                    onItemClick={onPresetClick}
                />
            </ContentSection>
            <SideSheet
                title={t("options")}
                open={true}
            >
                <SideSheetFilterInput
                    label={t("uiCustomizationPresetAdministration.tabs.presets.filterBy")}
                    value={filterText}
                    onChange={(value: string) => {
                        setFilterText(value);
                    }}
                />
                <SideSheetSelect
                    label={t("uiCustomizationPresetAdministration.sortBy")}
                    value={sortBy.toString()}
                    onChange={(value: string) => {
                        setSortBy(Number(value));
                    }}
                >
                    {presetSortByOptions.map((o, index) => {
                        return (
                            <MenuItem key={index}
                                value={o.value}
                            >
                                {o.label}
                            </MenuItem>
                        );
                    })}
                </SideSheetSelect>
            </SideSheet>
        </ContentContainer>
    );
}
